/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and international
 * treaties. Unauthorized use, reproduction, distribution, or modification of any content
 * from Knowledge for Decisions (K4D) is strictly prohibited. For inquiries regarding
 * the use or licensing of K4D's opyrighted materials, please contact us at
 * contact@k4d.com.br. "Knowledge for Decisions (K4D)" and the slogan "Using data to make
 * precise decisions" are registered trademarks of Knowledge for Decisions (K4D).
 */

import * as jose from "jose";
import { jwtVerify, importJWK, JWK } from "jose";
import { OpenIdService } from "@services/requests";

/**
 * verifyToken function.
 *
 * @param {*} token - Parameter description
 *
 * @returns {*} Return value description
 */
export async function verifyToken(token: string): Promise<boolean> {
  const jwks_datas = await getJWKS();
  const rsaKeyPublic = JSON.parse(JSON.stringify(jwks_datas)).keys[0];

  const rsaKeyPublicToBase64Url: JWK = {
    ...rsaKeyPublic,
    n: toBase64Url(rsaKeyPublic.n),
  };

  const key = await importJWK(rsaKeyPublicToBase64Url, "RS256");

  try {
    const { payload } = await jwtVerify(token, key);
    console.log("Token is valid:", payload);
    return true;
  } catch (err) {
    console.error("Token verification failed:", err);
  }
  return false;
}

/**
 * toBase64Url function.
 *
 * @param {*} base64 - Parameter description
 *
 * @returns {*} Return value description
 */
function toBase64Url(base64: string): string {
  return base64.replace(/=/g, "").replace(/\+/g, "-").replace(/\//g, "_");
}

/**
 * getJWKS function.
 *
 * @returns {*} Return value description
 */
async function getJWKS() {
  const cachedData = localStorage.getItem("jwks_datas");
  if (cachedData) {
    const { data, expiresAt } = JSON.parse(cachedData);
    if (Date.now() < expiresAt) {
      return data;
    }
  }

  const jwks_datas = await OpenIdService.getOpenIdjwks();
  localStorage.setItem(
    "jwks_datas",
    JSON.stringify({ data: jwks_datas, expiresAt: Date.now() + 86400000 }) // 24 horas
  );
  return jwks_datas;
}


/**
 * createJWTMock function.
 *
 * @param {*} mySuperKey - Parameter description
 *
 * @returns {*} Return value description
 */
export async function createJWTMock(mySuperKey: jose.JWK): Promise<string> {
  let jwt = "";
  try {
    const privateKey = await jose.importJWK(mySuperKey, "RS256");

    const payload = {
      iss: "your-issuer",
      sub: "your-subject",
      aud: "your-audience",
      exp: Math.floor(Date.now() / 1000) + 60 * 60, // 1 hour expiration
      iat: Math.floor(Date.now() / 1000),
      customClaim: "customValue",
    };

    jwt = await new jose.SignJWT(payload)
      .setProtectedHeader({ alg: "RS256" })
      .sign(privateKey);
  } catch (error) {
    console.error("Erro:", error);
  }
  console.log(jwt);
  return jwt;
}
